/**
 * LO QUE SE RELLENA SOLO AL CREAR UN TRATAMIENTO DESDE UN PARTE DE PLAGA.
 *
 * Vive aquí y no dentro de una pantalla porque «Crear tratamiento» sale ya de
 * varios sitios y las copias habían empezado a divergir: la del listado de
 * plagas busca la plaga que el responsable clasificó y las líneas observadas; la
 * de `PestAlertsPanel` manda `pest_id: null` y un título genérico, así que el
 * mismo botón deja el formulario a medias según por dónde hayas entrado.
 *
 * Al mapa se le añade el botón por petición del usuario (31-08-2026: «que abra
 * la ventana de crear tratamiento con los campos ya rellenos, no que me saque de
 * la página»), y escribir una TERCERA versión era exactamente la forma de que el
 * mapa naciera ya desalineado.
 *
 * PENDIENTE: `PestAlertsPanel` sigue con la suya. No se ha tocado aquí porque
 * cambiaría lo que rellena esa pantalla sin que nadie lo haya pedido, pero es
 * una migración de quince líneas y el sitio al que traerla es este.
 *
 * Pide a la base lo que no se puede deducir de lo que hay en pantalla (la
 * clasificación fresca y las líneas observadas): quien llama solo tiene que
 * traer la sesión y el traductor.
 */
import { addDays, format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { buildAutoTaskTitle } from "@/lib/taskTitle";

/** Lo que entiende `TaskFormDialog` cuando la tarea nace de un parte. */
export interface ScoutingPrefilledTaskData {
  scouting_session_id: string;
  greenhouse_id: string | null;
  block_id: string | null;
  pest_id: string | null;
  pest_name: string | null;
  scheduled_date: string;
  title: string;
  fromScouting?: boolean;
  observed_rows?: Record<string, { row_start: number | null; row_end: number | null }>;
}

/** Lo mínimo que hace falta saber del parte para rellenar el formulario. */
export interface TreatmentPrefillSession {
  id: string;
  greenhouse_id: string | null;
  block_id: string | null;
  /** Si ya se tienen a mano, se ahorran una consulta (no es obligatorio). */
  triage_pest_id?: string | null;
  triage_pest_name_snapshot?: string | null;
  /** La plaga observada en campo, cuando el responsable no ha clasificado aún. */
  mainObservation?: { pest_name: string } | null;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Construye el relleno, o `null` si el parte no tiene sector.
 *
 * Devolver `null` y no lanzar: sin sector la tarea nacería sin sitio donde
 * hacerse, y quien llama es el que sabe cómo avisar (un toast en una pantalla,
 * otra cosa en otra).
 */
export async function buildTreatmentPrefill(
  session: TreatmentPrefillSession,
  t: Translate,
): Promise<ScoutingPrefilledTaskData | null> {
  if (!session.block_id) return null;

  /* LA CLASIFICACIÓN, RECIÉN LEÍDA. Se acaba de clasificar en la ficha de al
     lado, así que lo que hay en memoria puede ser de hace un segundo pero de
     antes del cambio; y de la chincheta del mapa no viene nada de esto. Si la
     consulta falla se sigue con lo que trajera quien llama. */
  let pestId: string | null = session.triage_pest_id ?? null;
  let pestName: string | null = session.triage_pest_name_snapshot ?? null;
  const { data: fresh } = await supabase
    .from("pest_scouting_sessions")
    .select("triage_pest_id, triage_pest_name_snapshot")
    .eq("id", session.id)
    .maybeSingle();
  if (fresh?.triage_pest_id) {
    pestId = fresh.triage_pest_id;
    pestName = fresh.triage_pest_name_snapshot ?? pestName;
  }

  /* Sin clasificar, vale la plaga que se observó en campo: es lo que el
     formulario habría preguntado de todas formas. */
  if (!pestId && session.mainObservation) {
    const { data: obs } = await supabase
      .from("pest_scouting_observations")
      .select("pest_id, pest:pests(name)")
      .eq("scouting_session_id", session.id)
      .order("count", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (obs) {
      pestId = obs.pest_id;
      pestName = (obs.pest as { name?: string } | null)?.name ?? session.mainObservation.pest_name;
    }
  }

  /* Las líneas observadas: el tratamiento se hace donde se vio la plaga, no en
     el sector entero. */
  const { data: rowObs } = await supabase
    .from("pest_scouting_observations")
    .select("observed_row_start, observed_row_end")
    .eq("scouting_session_id", session.id)
    .not("observed_row_start", "is", null);

  const observedRows: ScoutingPrefilledTaskData["observed_rows"] =
    rowObs && rowObs.length > 0
      ? {
          [session.block_id]: {
            row_start: rowObs[0].observed_row_start,
            row_end: rowObs[0].observed_row_end,
          },
        }
      : undefined;

  return {
    scouting_session_id: session.id,
    greenhouse_id: session.greenhouse_id,
    block_id: session.block_id,
    pest_id: pestId,
    pest_name: pestName,
    // Mañana a esta hora: nadie trata en el mismo instante en que lo decide.
    scheduled_date: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
    // Título de partida con la regla común; el formulario lo recalcula al guardar
    // con el tratamiento que se elija (usuario 14-09-2026).
    title: buildAutoTaskTitle({ type: "treatment", pestName }, t),
    // Bloquea ubicación, nave, sector y plaga en el formulario: vienen del
    // parte y cambiarlos ahí rompería el vínculo entre los dos.
    fromScouting: true,
    observed_rows: observedRows,
  };
}
